import { useNavigate } from 'react-router-dom';
import { FlexContainer, StepperHeader, StepperBody, type Step } from '@uigovpe/components';
import { PageHeader } from '@/shared/components/PageHeader';
import { Skeleton } from '@/shared/components/Skeleton';
import { ErrorNotice } from '@/shared/components/ErrorNotice';
import { useRequestWizard, StageKey } from './hooks/useRequestWizard';
import { IncidentStep } from './components/steps/IncidentStep';
import { PersonalDataStep } from './components/steps/PersonalDataStep';
import { ContactStep } from './components/steps/ContactStep';
import { ReviewStep } from './components/steps/ReviewStep';
import { SubmittedStep } from './components/steps/SubmittedStep';

const STAGE_LABELS: Record<StageKey, string> = {
  incident: 'Ocorrência',
  personal: 'Seus dados',
  contact: 'Contato',
  review: 'Revisão',
};

export function RequestCertificatePage() {
  const navigate = useNavigate();
  const wizard = useRequestWizard();

  const header = (
    <PageHeader
      eyebrow="Certidão Digital"
      title="Solicitar certidão de ocorrência"
      description="Leva poucos minutos. O que você preencher fica salvo automaticamente."
    />
  );

  if (wizard.loading) {
    return (
      <>
        {header}
        <div className="cd-step-page">
          <div className="cd-wizard-skeleton__steps">
            {Array.from({ length: 4 }, (_, i) => (
              <Skeleton key={i} height="2.5rem" borderRadius="999px" />
            ))}
          </div>
          <section className="cd-step">
            <Skeleton width="10rem" height="1.5rem" />
            <FlexContainer direction="col" gap="6">
              <Skeleton height="2.75rem" />
              <div className="cd-grid cd-grid--2">
                <Skeleton height="2.75rem" />
                <Skeleton height="2.75rem" />
              </div>
              <Skeleton height="6rem" />
            </FlexContainer>
          </section>
        </div>
      </>
    );
  }

  if (wizard.submitted) {
    return (
      <SubmittedStep
        request={wizard.submitted}
        onTrack={() => navigate(`/my-requests/${wizard.submitted!.id}`)}
        onBackHome={() => navigate('/')}
      />
    );
  }

  const { profile, request, incidentForm, flowStages } = wizard;
  const ready = profile && request && incidentForm;

  const steps: Step[] = ready
    ? flowStages.map((key, index) => ({
        label: STAGE_LABELS[key],
        isConclusion: key === 'review',
        isCompleted: index < wizard.stageIndex,
        render: () => {
          switch (key) {
            case 'incident':
              return (
                <IncidentStep
                  value={incidentForm}
                  incidentTypes={wizard.incidentTypes}
                  autoSaveState={wizard.autoSaveState}
                  onChange={wizard.changeIncidentForm}
                  onNext={wizard.leaveIncidentStep}
                />
              );
            case 'personal':
              return (
                <PersonalDataStep
                  profile={profile}
                  busy={wizard.busy}
                  error={wizard.stepError}
                  onSave={wizard.savePersonalData}
                  onBack={wizard.back}
                />
              );
            case 'contact':
              return (
                <ContactStep
                  profile={profile}
                  busy={wizard.busy}
                  error={wizard.stepError}
                  codeRecipient={wizard.codeRecipient}
                  onSaveContact={wizard.saveContact}
                  onSendCode={wizard.sendVerificationCode}
                  onVerifyCode={wizard.verifyCode}
                  onBack={wizard.back}
                  onNext={wizard.next}
                />
              );
            case 'review':
            default:
              return (
                <ReviewStep
                  request={request}
                  profile={profile}
                  incidentTypes={wizard.incidentTypes}
                  busy={wizard.busy}
                  error={wizard.stepError}
                  onEditIncident={() => wizard.goToStage('incident')}
                  onEditPersonalData={
                    flowStages.includes('personal')
                      ? () => wizard.goToStage('personal')
                      : () => navigate('/profile')
                  }
                  onEditContact={
                    flowStages.includes('contact')
                      ? () => wizard.goToStage('contact')
                      : () => navigate('/profile')
                  }
                  onBack={wizard.back}
                  onSubmit={wizard.submit}
                />
              );
          }
        },
      }))
    : [];

  return (
    <>
      {header}

      <ErrorNotice message={wizard.loadError} />

      {!wizard.loadError && ready && (
        <>
          <StepperHeader
            steps={steps}
            currentStep={wizard.stageIndex}
            providerName="Corpo de Bombeiros Militar de Pernambuco"
            processName="Solicitação de certidão"
            minimal
            onChangeStep={(index) => {
              // Só deixa voltar para uma etapa já concluída — avançar
              // depende de validação que acontece no servidor.
              if (index < wizard.stageIndex) wizard.goToStage(flowStages[index]);
            }}
          />
          <StepperBody steps={steps} currentStep={wizard.stageIndex} />
        </>
      )}
    </>
  );
}
